'use client';
import { motion } from 'framer-motion';
import { profile } from '@/data/profile';
import { FaGithub, FaLinkedin, FaKaggle, FaChevronDown, FaChevronUp, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import { NetworkConstellation } from '../ui/NetworkConstellation';
import { TextConstellation } from '../ui/TextConstellation';
import { useState, useEffect } from 'react';
import OrbitalObject from '../ui/OrbitalObject';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100); // Show scroll to top after 100px scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Create 10 different orbital objects with varying properties
  const orbitalConfigs = [
    { color: "#4f83ff", size: 12, orbitDuration: 25, orbitRadius: 200, delay: 0 },
    { color: "#83ff4f", size: 8, orbitDuration: 20, orbitRadius: 150, delay: 3 },
    { color: "#ff4f83", size: 10, orbitDuration: 30, orbitRadius: 250, delay: 5 },
    { color: "#ff834f", size: 6, orbitDuration: 18, orbitRadius: 120, delay: 8 },
    { color: "#834fff", size: 14, orbitDuration: 35, orbitRadius: 300, delay: 2 },
    { color: "#4fff83", size: 9, orbitDuration: 22, orbitRadius: 180, delay: 10 },
    { color: "#ff4fff", size: 7, orbitDuration: 28, orbitRadius: 220, delay: 6 },
    { color: "#ffff4f", size: 11, orbitDuration: 26, orbitRadius: 160, delay: 12 },
    { color: "#4fffff", size: 5, orbitDuration: 15, orbitRadius: 100, delay: 4 },
    { color: "#ffffff", size: 13, orbitDuration: 40, orbitRadius: 350, delay: 7 }
  ];

  return (
    <section className="min-h-screen flex items-center justify-center pt-16 bg-black relative overflow-hidden">
      {/* Map through 10 different orbital objects */}
      {orbitalConfigs.map((config, index) => (
        <OrbitalObject
          key={index}
          color={config.color}
          size={config.size}
          orbitDuration={config.orbitDuration}
          orbitRadius={config.orbitRadius}
          delay={config.delay}
        />
      ))}

      <NetworkConstellation />
      <TextConstellation />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className="text-center mt-32 md:mt-40"
        >
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-white"
          >
            {profile.title}
          </h1>
          <p
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8"
          >
            {profile.summary}
          </p>
          <motion.div
            className="flex justify-center space-x-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <motion.a
              whileHover={{ scale: 1.1, y: -3 }}
              href={profile.contact.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit my GitHub profile"
              className="text-3xl text-gray-300 hover:text-white transition-colors"
            >
              <FaGithub />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1, y: -3 }}
              href={profile.contact.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect with me on LinkedIn"
              className="text-3xl text-gray-300 hover:text-blue-400 transition-colors"
            >
              <FaLinkedin />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1, y: -3 }}
              href={profile.contact.kaggle}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View my Kaggle profile"
              className="text-3xl text-gray-300 hover:text-blue-300 transition-colors"
            >
              <FaKaggle />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1, y: -3 }}
              href={`https://wa.me/${profile.contact.whatsapp.replace(/[^\d]/g, '')}?text=${encodeURIComponent('Hi! I found your portfolio and would like to connect.')}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact me on WhatsApp"
              className="text-3xl text-gray-300 hover:text-green-400 transition-colors"
            >
              <FaWhatsapp />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1, y: -3 }}
              href={`mailto:${profile.contact.email}`}
              aria-label="Send me an email"
              className="text-3xl text-gray-300 hover:text-red-400 transition-colors"
            >
              <FaEnvelope />
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Down Indicator - Only show when not scrolled */}
      {!isScrolled && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.8, delay: 2.0 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center text-white/70 cursor-pointer hover:text-white transition-colors"
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-sm mb-2 font-medium">Scroll Down</span>
            <FaChevronDown className="text-xl" />
          </motion.div>
        </motion.div>
      )}

      {/* Scroll To Top Button - Only show when scrolled */}
      {isScrolled && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-8 right-8 z-50"
        >
          <motion.button
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToTop}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full p-4 text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/20"
          >
            <FaChevronUp className="text-xl" />
          </motion.button>
        </motion.div>
      )}
    </section>
  );
}